import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/dealership/lib/api";
import { toast } from "sonner";
import PageHeader from "@/dealership/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import LogActivityDialog from "@/dealership/components/LogActivityDialog";
import GmailLeadsBanner from "@/dealership/components/GmailLeadsBanner";
import VehicleSelect from "@/dealership/components/VehicleSelect";
import { stageStyle, fmtDate } from "@/dealership/lib/funnel";
import { Plus, Pencil, Trash2, Phone, Mail, Car, DollarSign, User, Tag, Clock, StickyNote, ArrowUpDown, ArrowUp, ArrowDown, ArrowRightLeft } from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Negotiating", "Won", "Lost"];
const SOURCES = ["Walk-in", "Website", "Phone", "Referral", "AutoTrader", "CarGurus", "Facebook", "Other"];

const statusStyle = {
  New: "bg-blue-500/12 text-blue-400",
  Contacted: "bg-indigo-500/12 text-indigo-400",
  Qualified: "bg-amber-500/12 text-amber-400",
  Negotiating: "bg-purple-500/12 text-purple-400",
  Won: "bg-green-500/12 text-green-400",
  Lost: "bg-rose-500/12 text-rose-400",
};

const empty = {
  name: "", email: "", phone: "", source: "Walk-in", status: "New",
  vehicle_interest: "", budget: "", assigned_to: "", notes: "",
};

function fmtDateTime(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return d; }
}

function SortHead({ label, sortKey, sort, onSort, className = "" }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        onClick={() => onSort(sortKey)}
        data-testid={`sort-${sortKey}`}
        className={`flex items-center gap-1.5 hover:text-foreground transition-colors ${active ? "text-foreground font-semibold" : ""}`}
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? "text-primary" : "text-muted-foreground/60"}`} />
      </button>
    </TableHead>
  );
}

export default function Leads() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads"],
    queryFn: async () => (await api.get("/leads")).data,
  });

  useEffect(() => {
    const eid = searchParams.get("edit");
    if (eid && leads.length) {
      const l = leads.find((x) => x.id === eid);
      if (l) { openEdit(l); setSearchParams({}, { replace: true }); }
    }
  }, [searchParams, leads]); // eslint-disable-line

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sortedLeads = (() => {
    if (!sort.key) return leads;
    const arr = [...leads];
    arr.sort((a, b) => {
      const av = (a[sort.key] ?? "").toString().toLowerCase();
      const bv = (b[sort.key] ?? "").toString().toLowerCase();
      // empty values always go last
      if (!av && bv) return 1;
      if (av && !bv) return -1;
      return av.localeCompare(bv);
    });
    return sort.dir === "desc" ? arr.reverse() : arr;
  })();

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      const body = { ...payload, budget: payload.budget === "" ? null : Number(payload.budget) };
      if (editing) return (await api.put(`/leads/${editing}`, body)).data;
      return (await api.post("/leads", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(editing ? "Lead updated" : "Lead added");
      setOpen(false);
    },
    onError: () => toast.error("Failed to save lead"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/leads/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Lead deleted");
    },
  });

  const convertMut = useMutation({
    mutationFn: async (id) => (await api.post(`/leads/${id}/convert`)).data,
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Lead converted to a deal — opening it for review.");
      navigate(`/dealership/deals?edit=${deal.id}`);
    },
    onError: () => toast.error("Failed to convert lead"),
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l.id);
    setForm({ ...l, budget: l.budget ?? "" });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-8">
      <PageHeader
        title="Leads"
        subtitle="Track prospects from first contact to closed deal."
        action={
          <Button onClick={openNew} data-testid="add-lead-button">
            <Plus className="h-4 w-4 mr-2" /> Add Lead
          </Button>
        }
      />

      <GmailLeadsBanner />

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHead label="Name" sortKey="name" sort={sort} onSort={toggleSort} />
              <TableHead>Contact</TableHead>
              <SortHead label="Vehicle Interest" sortKey="vehicle_interest" sort={sort} onSort={toggleSort} />
              <SortHead label="Source" sortKey="source" sort={sort} onSort={toggleSort} />
              <SortHead label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
              <SortHead label="Assigned" sortKey="assigned_to" sort={sort} onSort={toggleSort} />
              <SortHead label="Received" sortKey="created_at" sort={sort} onSort={toggleSort} />
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : sortedLeads.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No leads yet. Add your first lead.</TableCell></TableRow>
            ) : (
              sortedLeads.map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="cursor-pointer" onClick={() => navigate(`/dealership/leads/${l.id}`)}>
                  <TableCell className="font-semibold">{l.name}</TableCell>
                  <TableCell>
                    <div className="text-xs space-y-1 text-muted-foreground">
                      {l.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</div>}
                      {l.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</div>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{l.vehicle_interest || "—"}</TableCell>
                  <TableCell className="text-sm">{l.source}</TableCell>
                  <TableCell>
                    <Badge className={`${statusStyle[l.status]} hover:${statusStyle[l.status]} border-0`}>{l.status}</Badge>
                    {l.auto_replied && <Badge className="bg-violet-500/12 text-violet-400 border-0 ml-1" title={`AI auto-reply sent ${l.auto_reply_at ? new Date(l.auto_reply_at).toLocaleString() : ""}`} data-testid={`lead-ai-replied-${l.id}`}>AI replied</Badge>}
                  </TableCell>
                  <TableCell className="text-sm">{l.assigned_to || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(l.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {l.status !== "Won" && (
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); if (window.confirm(`Convert "${l.name}" into a deal? This marks the lead as Won.`)) convertMut.mutate(l.id); }} disabled={convertMut.isPending} data-testid={`convert-lead-${l.id}`} title="Convert to deal">
                        <ArrowRightLeft className="h-4 w-4 text-emerald-400" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(l); }} data-testid={`edit-lead-${l.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); delMut.mutate(l.id); }} data-testid={`delete-lead-${l.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Lead" : "Add Lead"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-2">
              <Label className="label-mono">Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="lead-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-mono">Phone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-mono">Email</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email-input" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="label-mono">Vehicle Interest</Label>
              <VehicleSelect value={form.vehicle_interest} onChange={(v) => set("vehicle_interest", v)} testId="lead-vehicle-select" placeholder="Select from inventory" />
            </div>
            <div className="space-y-2">
              <Label className="label-mono">Budget (CAD)</Label>
              <Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="lead-budget-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-mono">Assigned To</Label>
              <Input value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} data-testid="lead-assigned-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-mono">Source</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-mono">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="label-mono">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="lead-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(form)} disabled={!form.name || saveMut.isPending} data-testid="save-lead-button">
              {editing ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-words">{value}</p>
      </div>
    </div>
  );
}

function LeadDetail({ lead, onClose, onEdit }) {
  const [logOpen, setLogOpen] = useState(false);
  const { data: activities = [] } = useQuery({
    queryKey: ["activities", lead?.id],
    queryFn: async () => (await api.get("/activities", { params: { lead_id: lead.id } })).data,
    enabled: !!lead,
  });
  if (!lead) return null;
  const noteLines = (lead.notes || "")
    .split(/\s*\|\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  const fmt = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleString(); } catch { return d; }
  };
  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-8">
            <DialogTitle className="font-heading text-2xl">{lead.name}</DialogTitle>
            <Badge className={`${statusStyle[lead.status]} border-0`}>{lead.status}</Badge>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6">
          <DetailRow icon={Phone} label="Phone" value={lead.phone} />
          <DetailRow icon={Mail} label="Email" value={lead.email} />
          <DetailRow icon={Car} label="Vehicle Interest" value={lead.vehicle_interest} />
          <DetailRow icon={DollarSign} label="Budget" value={lead.budget != null ? `$${Number(lead.budget).toLocaleString()}` : ""} />
          <DetailRow icon={Tag} label="Source" value={lead.source} />
          <DetailRow icon={User} label="Assigned To" value={lead.assigned_to} />
          <DetailRow icon={Clock} label="Added" value={fmt(lead.created_at)} />
          <DetailRow icon={Clock} label="Last Updated" value={fmt(lead.updated_at)} />
        </div>

        {lead.auto_replied && (
          <div className="rounded-md border border-violet-500/25 bg-violet-500/[0.07] px-3 py-2.5 text-sm" data-testid="lead-auto-reply-info">
            <div className="font-medium text-violet-400 mb-1">AI auto-reply sent {lead.auto_reply_at ? `· ${fmt(lead.auto_reply_at)}` : ""}</div>
            <div className="text-xs text-muted-foreground mb-1">Subject: {lead.auto_reply_subject || "—"}</div>
            {lead.auto_reply_body && <div className="text-xs text-foreground/80 max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: lead.auto_reply_body }} />}
          </div>
        )}

        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <StickyNote className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Notes</p>
          </div>
          {noteLines.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No notes for this lead.</p>
          ) : (
            <div className="rounded-md bg-secondary/50 border border-border p-4 space-y-2" data-testid="lead-detail-notes">
              {noteLines.map((line, i) => (
                <p key={i} className="text-sm leading-relaxed flex gap-2">
                  <span className="text-primary mt-1.5 h-1 w-1 rounded-full bg-primary shrink-0" />
                  <span className="flex-1">{line}</span>
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Activity Timeline</p>
            <Button size="sm" variant="outline" onClick={() => setLogOpen(true)} data-testid="lead-log-activity-button">
              <Plus className="h-3.5 w-3.5 mr-1" /> Log Activity
            </Button>
          </div>
          {activities.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No activities yet. Log the first touchpoint.</p>
          ) : (
            <div className="space-y-2" data-testid="lead-activity-timeline">
              {activities.map((a) => (
                <div key={a.id} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <Badge className={`${stageStyle[a.type] || "bg-zinc-500/12 text-zinc-400"} border-0 text-xs`}>{a.type}</Badge>
                    <span className="text-xs text-muted-foreground">{fmt(a.created_at)}</span>
                  </div>
                  {a.notes && <p className="text-sm mt-2 break-words">{a.notes}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    <span>{a.assignee_name}</span>
                    {a.follow_up_date && !a.follow_up_done && <span className="text-primary">Follow-up {fmtDate(a.follow_up_date)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => onEdit(lead)} data-testid="lead-detail-edit-button">
            <Pencil className="h-4 w-4 mr-2" /> Edit Lead
          </Button>
        </DialogFooter>
      </DialogContent>
      <LogActivityDialog open={logOpen} onOpenChange={setLogOpen} presetLead={{ id: lead.id, name: lead.name }} />
    </Dialog>
  );
}
